import { useEffect, useState } from 'react';
import {
  PatientDetailPrescriptionItem,
  PrescriptionActionHookReturn,
  PrescriptionItemToCreate,
  ReactSelectOptionProp,
} from '../interface/interface';
import {
  fetchDoctorAndProducts,
  updatePrescriptionList,
  updatePrescriptionStatus,
} from '../service';
import moment from 'moment';
import { v4 } from 'uuid';
import { SingleValue } from 'react-select';
import showToast from '../components/ToastComponent';

export const useToHandlePrescriptionActions = (
  accessToken: string,
  patientId: number,
): PrescriptionActionHookReturn => {
  const [prescriptionList, setPrescriptionList] = useState<
    Array<PatientDetailPrescriptionItem>
  >([]);
  const [isModalOpen, setModalVisibility] = useState<boolean>(false);

  // Doctor and Patient Data
  const [isPatientDoctorLoading, setPatientDoctorLoadStatus] =
    useState<boolean>(false);
  const [doctorList, setDoctorList] = useState<Array<ReactSelectOptionProp>>(
    [],
  );
  const [productList, setProductList] = useState<Array<ReactSelectOptionProp>>(
    [],
  );
  const [reRenderCounter, setReRenderCounter] = useState<number>(0);

  // States for holding dynamic prescription
  const [createPrescriptionDataList, setCreatePrescriptionDataList] = useState<
    Array<PrescriptionItemToCreate>
  >([createEmptyPrescriptionFields()]);

  function createEmptyPrescriptionFields(): PrescriptionItemToCreate {
    return {
      id: v4().split('-').pop() as string,
      created_date: moment().toDate(),
      quantity: '',
      repeats: '',
      interval: '',
      eRxToken: '',
      dosage: '',
      product: {} as ReactSelectOptionProp,
      doctor: {} as ReactSelectOptionProp,
    };
  }

  async function handleSubmit(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) {
    try {
      e.preventDefault();

      if (
        !createPrescriptionDataList.every((x) => x.product.value) ||
        !createPrescriptionDataList.every((x) => x.quantity) ||
        !createPrescriptionDataList.every((x) => x.repeats)
      ) {
        showToast({
          message:
            'Please complete all mandatory fields for prescription creation.',
          type: 'warning',
        });
        return;
      }

      const status = await updatePrescriptionList(accessToken, {
        prescriptions: createPrescriptionDataList.map((e: any) => {
          delete e.id;
          return { ...e, patient_id: patientId };
        }),
      });

      if (status === 201) {
        showToast({
          message: 'Prescription created successfully',
          type: 'success',
        });

        setModalVisibility(false);
        setReRenderCounter((p) => p + 1);
      } else {
        showToast({
          message: 'Error while creating prescription',
          type: 'error',
        });
      }
    } catch (e) {
      console.log('Error on Submission', e);
    }
  }

  async function updatePrescriptionStatusHandler(
    selectedOption: SingleValue<{ label: string; value: string }>,
    id: number,
  ) {
    // Get prescriptions from the existing lis
    const presForStatusUpdate = prescriptionList.find((ps) => ps.id === id);
    if (!presForStatusUpdate) {
      console.log('Prescription match not found');
      return;
    }

    const resp = await updatePrescriptionStatus(accessToken, {
      prescripionId: presForStatusUpdate.id,
      staus: selectedOption?.value as string,
    });

    if (resp === 200) {
      showToast({
        message: 'Prescription status updated successfully',
        type: 'success',
      });
      setReRenderCounter((p) => p + 1);
    } else {
      showToast({
        message: 'Unable to update prescription status',
        type: 'info',
      });
    }
  }

  useEffect(() => {
    if (!isModalOpen || !accessToken) return;

    setPatientDoctorLoadStatus(true);

    // Call for products and doctors
    fetchDoctorAndProducts(accessToken).then((data): void => {
      if (typeof data === 'number') {
        showToast({
          message: 'There is an error on getting doctor and product',
          type: 'error',
        });

        setPatientDoctorLoadStatus(false);
        return;
      }

      setProductList([
        ...data.products.map((e) => ({
          label: e.name,
          value: e.id.toString(),
        })),
      ]);

      setDoctorList([
        ...data.doctors.map((d) => ({ label: d.name, value: d.id.toString() })),
      ]);

      setPatientDoctorLoadStatus(false);
    });
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen) {
      setCreatePrescriptionDataList([createEmptyPrescriptionFields()]);
    }
  }, [isModalOpen]);

  return [
    { prescriptionList, setPrescriptionList },
    {
      modalState: isModalOpen,
      handleModal: (state) => setModalVisibility(state),
    },
    {
      isLoading: isPatientDoctorLoading,
      productList,
      doctorList,
    },
    {
      item: createPrescriptionDataList,
      addPrescription: () =>
        setCreatePrescriptionDataList((p) => [
          ...p,
          createEmptyPrescriptionFields(),
        ]),

      removePrescription: (id: string) =>
        setCreatePrescriptionDataList((p) => p.filter((e) => e.id !== id)),

      handleInput: (e, id) =>
        setCreatePrescriptionDataList((ps) => {
          const modifiedContainer = ps.find((pse) => pse.id === id);
          if (!modifiedContainer) return ps;

          (modifiedContainer as any)[e.target.name] = e.target.value;

          return ps.flatMap((f) => (f.id === id ? [modifiedContainer] : [f]));
        }),

      handleDate: (date, id) =>
        setCreatePrescriptionDataList((ps) => {
          const modifiedContainer = ps.find((pse) => pse.id === id);
          if (!modifiedContainer) return ps;

          modifiedContainer.created_date = date;

          return ps.flatMap((f) => (f.id === id ? [modifiedContainer] : [f]));
        }),

      handleSelect: (selectedOption, name, id) =>
        setCreatePrescriptionDataList((ps) => {
          const modifiedContainer = ps.find((pse) => pse.id === id);
          if (!modifiedContainer) return ps;

          (modifiedContainer as any)[name] = selectedOption;

          return ps.flatMap((f) => (f.id === id ? [modifiedContainer] : [f]));
        }),
      handleSubmit,
      updatePrescriptionStatus: (option, id) =>
        updatePrescriptionStatusHandler(option, id),
    },
    reRenderCounter,
    setReRenderCounter,
  ];
};
